import { LocalizationProvider } from '@localization/LocalizationProvider';
import styles from './NumberRangeComponent.module.scss';

import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid.tsx';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { GridApi } from 'ag-grid-enterprise';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider, useSelector } from 'react-redux';
import { RowSelectionMode } from '@models/enums/RowSelectionMode';
import { PermissionProvider } from '@auth/PermissionProvider.tsx';
import { useInjectReducers } from '@hooks/useInjectReducers.ts';
import { ApiCallType } from '@api/ApiCalls.ts';
import { useNumberRangesPermissions } from '@hooks/permissions/useNumberRangesPermissions.ts';
import { useGlobalNumberRangesPermissions } from '@hooks/permissions/useGlobalNumberRangesPermissions.ts';
import { NumberRange } from '@models/DomainModels/Master/number-range.ts';
import { gridColumnDefinitions } from './grid-column-definitions';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@commonComponents/core/ErrorFallback';
import { logger } from '@utils/logging-utils';
import { selectIsBrzFinanceEnabled } from '@state/app-context-slice';
import { NumberRangeScope } from '@models/enums/NumberRangeScope';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

import { ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import brzTheme from '@brz/brz-global-mui-theme';
import { Typography } from '@mui/material';
import { NumberRangeDetailDialogWrapper } from './NumberRangeDetailDialog';

interface NumberRangesComponentProps {
  readonly organizationId: string;
  readonly store: DynamicStore | undefined;
}

const extendedAG_GRID_LOCALE_DE = {
  ...AG_GRID_LOCALE_DE,
  ariaHeaderSelection: ''
};

/**
 * Component for managing number ranges
 * Displays a grid of number ranges with CRUD operations
 */
function NumberRangesComponent({ organizationId, store }: Readonly<NumberRangesComponentProps>) {
  const { writePermission } = useNumberRangesPermissions(organizationId);
  const { writePermission: globalWritePermission, readPermission: globalReadPermission } = useGlobalNumberRangesPermissions(organizationId);
  const isBrzFinanceEnabled = useSelector(selectIsBrzFinanceEnabled);

  const { t, i18n } = useTranslation('numberRanges');

  const [localeText, setLocaleText] = useState(AG_GRID_LOCALE_EN);
  const [gridKey, setGridKey] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridRef = useRef<{
    refresh: () => void;
    addCreatedItem: (item: NumberRange) => void;
    updateModifiedItem: (item: NumberRange) => void;
  }>(null);
  const selectedIdsRef = useRef<string[]>([]);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedNumberRangeData, setSelectedNumberRangeData] = useState<NumberRange | undefined>(undefined);
  const [isChecked, setIsChecked] = useState(true);

  const showCheckBox = useMemo(() => globalWritePermission || globalReadPermission, [globalWritePermission, globalReadPermission]);

  // Only pass true to the backend when the user actually has the right to see global ranges
  const effectiveIsChecked = useMemo(() => showCheckBox && isChecked, [showCheckBox, isChecked]);

  /**
   * Handle language change and update the grid locale
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLocaleText = lng === 'de' ? extendedAG_GRID_LOCALE_DE : AG_GRID_LOCALE_EN;
      setLocaleText(newLocaleText);
      setGridKey(prevKey => prevKey + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Grid ready callback to store the grid API reference
   */
  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  }, []);

  /**
   * Handle clicking on a number range profile
   * @param data The number range data
   */
  const handleProfileClick = useCallback((data: NumberRange) => {
    if (!data?.id) {
      logger.warn('NumberRangesComponent', 'Profile click with invalid data', data);
      return;
    }

    // Store the selected ID for later use if needed
    selectedIdsRef.current = [data.id];
    setSelectedNumberRangeData(data);
    setProfileDialogOpen(true);
  }, []);

  const handleProfileClose = useCallback((result: EntityDialogCloseResult<NumberRange>) => {
    setProfileDialogOpen(false);

    if (result.action === 'created' && result.data) {
      gridRef.current?.addCreatedItem(result.data);
    } else if (result.action === 'updated' && result.data) {
      gridRef.current?.updateModifiedItem(result.data);
    }

    setSelectedNumberRangeData(undefined);
  }, []);

  /**
   * Memoized column definitions to prevent unnecessary re-renders
   */
  const columnDefs = useCallback(() => gridColumnDefinitions({ t }), [t]);

  /**
   * Handle clicking the add button to create a new number range
   */
  const handleAddButtonClick = useCallback(() => {
    setSelectedNumberRangeData(undefined);
    setProfileDialogOpen(true);
  }, []);

  /**
   * Determine if a number range row should be disabled for deletion
   * Business partner number ranges cannot be deleted when BRZ Finance is enabled
   * Global number ranges require global write permission
   */
  const isRowDisabled = useCallback(
    (numberRange: NumberRange): boolean => {
      if (!writePermission) return true;

      // If BRZ Finance is enabled, BusinessPartner number ranges cannot be deleted
      if (isBrzFinanceEnabled && numberRange.scope === NumberRangeScope.BusinessPartner) {
        return true;
      }

      // Global number ranges can only be modified with global write permission
      if (numberRange.organizationId === null) {
        return !globalWritePermission;
      }

      return false;
    },
    [writePermission, isBrzFinanceEnabled, globalWritePermission]
  );

  return (
    <Grid className={styles.numberRangesComponent}>
      <Typography variant="h4" component="h1">
        {t('title')}
      </Typography>
      <Grid className={styles.content}>
        <Grid className={classNames(styles.grid)}>
          <CdfGrid<NumberRange>
            ref={gridRef}
            apiCallType={ApiCallType.FetchNumberRanges}
            key={gridKey}
            columnDefs={columnDefs()}
            gridOptions={{ onGridReady, localeText }}
            rowSelection={RowSelectionMode.Multiple}
            showAddButton={writePermission}
            showDeleteButton={writePermission}
            showDeleteButtonInGrid={writePermission}
            onAddClick={handleAddButtonClick}
            addButtonText={t('addButtonText')}
            onDoubleClick={event => event.data && handleProfileClick(event.data)}
            customRowDisabled={isRowDisabled}
            showCheckBox={showCheckBox}
            isChecked={effectiveIsChecked}
            onCheckBoxChange={setIsChecked}
            addCheckBoxLabel={t('includeGlobalNumberRanges')}
          />
        </Grid>
      </Grid>
      {profileDialogOpen && (
        <NumberRangeDetailDialogWrapper
          numberRangeData={selectedNumberRangeData}
          open={profileDialogOpen}
          onClose={handleProfileClose}
          organizationId={organizationId}
          store={store}
        />
      )}
    </Grid>
  );
}

/**
 * Wrapper component for NumberRangesComponent
 * Provides necessary context providers and error boundaries
 */
export function NumberRangesComponentWrapper({ organizationId, store }: Readonly<NumberRangesComponentProps>) {
  const reducerInjected = useInjectReducers(store, organizationId);
  if (!reducerInjected) {
    return null;
  }

  return (
    <ThemeProvider theme={brzTheme}>
      <LocalizationProvider>
        {store && (
          <Provider store={store}>
            <PermissionProvider>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <NumberRangesComponent organizationId={organizationId} store={store} />
              </ErrorBoundary>
            </PermissionProvider>
          </Provider>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}
